import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isOidcAuth } from '../config/featureFlags';
import { PATHS } from '../constants/Paths.constants';
import { useAppDispatch } from '../hooks/redux';
import { userManager } from '../components/OidcAuthProvider';
import {
  clearLogoutInProgress,
  isLogoutInProgress,
  logoutAndClearStorage,
  setLogoutInProgress
} from '../utils/Auth.util';

export function LogoutPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const handled = useRef(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const finalizeLogout = async () => {
      try {
        await userManager.removeUser();
      } catch {
        // Best effort cleanup only.
      } finally {
        dispatch(logoutAndClearStorage());
        clearLogoutInProgress();
        navigate(PATHS.DASHBOARD, { replace: true });
      }
    };

    const run = async () => {
      if (!isOidcAuth()) {
        dispatch(logoutAndClearStorage());
        navigate(PATHS.LOGIN, { replace: true });
        return;
      }

      const params = new URLSearchParams(location.search);
      const hasCallbackParams =
        params.has('state') ||
        params.has('error') ||
        params.has('iss') ||
        params.has('session_state');

      const logoutInProgress = isLogoutInProgress();

      if (logoutInProgress && hasCallbackParams) {
        try {
          await userManager.signoutRedirectCallback();
        } catch (callbackError) {
          console.error('[OIDC] Logout callback error:', callbackError);
        }

        await finalizeLogout();
        return;
      }

      if (logoutInProgress && !hasCallbackParams) {
        // Prevent signout redirect loops when provider returns to /logout
        // without callback params.
        await finalizeLogout();
        return;
      }

      setLogoutInProgress();
      dispatch(logoutAndClearStorage());

      try {
        await userManager.signoutRedirect();
      } catch (signoutError) {
        console.error('[OIDC] signoutRedirect error:', signoutError);
        setError('Sign out failed. Please try again.');
        await finalizeLogout();
      }
    };

    void run();
  }, [dispatch, location.search, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <p className="text-gray-700 text-lg font-medium">Signing out...</p>
        {error && <p className="text-red-600 mt-2">{error}</p>}
      </div>
    </div>
  );
}
